// Narrava voice — Google Cloud TTS via backend, fallback to browser.

export const hasTTS = typeof window !== "undefined" && "speechSynthesis" in window;
export const hasSR =
  typeof window !== "undefined" &&
  (window.SpeechRecognition || window.webkitSpeechRecognition);

let _unlocked = false;
let _currentAudio = null;

/**
 * Prime the TTS audio context from within a user gesture so subsequent
 * programmatic speak() calls aren't blocked by browser autoplay policies.
 * Safe to call multiple times.
 */
export function unlockSpeech() {
  if (!hasTTS || _unlocked) return;
  try {
    const u = new SpeechSynthesisUtterance("");
    u.volume = 0;
    window.speechSynthesis.speak(u);
    window.speechSynthesis.cancel();
    _unlocked = true;
  } catch {
    /* ignore */
  }
}

export const isSpeechUnlocked = () => _unlocked;

/**
 * Speak text via backend Google Cloud TTS. Falls back to browser TTS if backend fails.
 */
export function speak(text, { tradition = "krishna", gender = "female" } = {}) {
  if (!text) return () => {};
  stopSpeaking();

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

  fetch(`${backendUrl}/api/tts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, tradition, gender }),
  })
    .then((res) => {
      if (!res.ok) throw new Error(`TTS ${res.status}`);
      return res.blob();
    })
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      _currentAudio = audio;
      audio.onended = () => {
        URL.revokeObjectURL(url);
        _currentAudio = null;
      };
      audio.play().catch((e) => {
        console.warn("Audio play blocked:", e);
      });
    })
    .catch((err) => {
      console.warn("Backend TTS failed, falling back to browser:", err);
      _speakBrowser(text, { gender });
    });

  return () => stopSpeaking();
}

function _speakBrowser(text, { gender = "female" } = {}) {
  if (!hasTTS || !text) return;
  const clean = text
    .replace(/FEEL\s*:/gi, "")
    .replace(/STORY\s*:/gi, "")
    .replace(/WISDOM\s*:/gi, "")
    .trim();
  const utter = new SpeechSynthesisUtterance(clean);
  utter.rate = 0.92;
  utter.pitch = gender === "female" ? 1.05 : 0.9;

  const pickVoice = () => {
    const voices = window.speechSynthesis.getVoices();
    const preferLangs = ["en-IN", "en-GB", "en-US"];
    for (const lang of preferLangs) {
      const matches = voices.filter((v) => v.lang === lang);
      if (!matches.length) continue;
      const byGender = matches.find((v) =>
        gender === "female"
          ? /female|samantha|zira|aria|jenny|kate|serena/i.test(v.name)
          : /male|david|mark|guy|alex/i.test(v.name)
      );
      utter.voice = byGender || matches[0];
      return;
    }
    if (voices.length) utter.voice = voices[0];
  };

  if (window.speechSynthesis.getVoices().length === 0) {
    window.speechSynthesis.onvoiceschanged = pickVoice;
  } else {
    pickVoice();
  }

  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(utter);
}

export function stopSpeaking() {
  if (hasTTS) {
    try { window.speechSynthesis.cancel(); } catch {}
  }
  if (_currentAudio) {
    try { _currentAudio.pause(); } catch {}
    _currentAudio = null;
  }
}

export function createRecognition({ lang = "en-IN", onResult, onEnd, onError } = {}) {
  if (!hasSR) return null;
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  const rec = new SR();
  rec.lang = lang;
  rec.interimResults = true;
  rec.continuous = false;
  rec.onresult = (e) => {
    let interim = "";
    let final = "";
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const t = e.results[i][0].transcript;
      if (e.results[i].isFinal) final += t;
      else interim += t;
    }
    onResult?.({ interim, final });
  };
  rec.onend = () => onEnd?.();
  rec.onerror = (e) => onError?.(e);
  return rec;
}